import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Sun, Moon } from 'lucide-react'
import { motion } from 'framer-motion'
import { useAuthStore } from '@/stores/authStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useThemeStore } from '@/stores/themeStore'
import { NotificationPanel } from './NotificationPanel'

// Mini chip logo for top bar (scaled down from LoginScreen)
function ChipLogo() {
  return (
    <div className="relative w-7 h-7 flex-shrink-0">
      {/* Shared defs */}
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="navChipGold" x1="0" y1="0" x2="28" y2="28" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#F5D78E" />
            <stop offset="40%" stopColor="#D4A843" />
            <stop offset="100%" stopColor="#A67C2E" />
          </linearGradient>
          <linearGradient id="navChipInner" x1="5" y1="5" x2="23" y2="23" gradientUnits="userSpaceOnUse">
            <stop offset="0%" style={{ stopColor: 'var(--chip-inner-from)' }} />
            <stop offset="100%" style={{ stopColor: 'var(--chip-inner-to)' }} />
          </linearGradient>
          <linearGradient id="navTextGold" x1="9" y1="8" x2="19" y2="20" gradientUnits="userSpaceOnUse">
            <stop offset="0%" style={{ stopColor: 'var(--shimmer-highlight)' }} />
            <stop offset="50%" style={{ stopColor: 'var(--shimmer-base)' }} />
            <stop offset="100%" style={{ stopColor: 'var(--shimmer-base)' }} />
          </linearGradient>
        </defs>
      </svg>

      {/* Outer ring + notches — slow clockwise */}
      <motion.svg
        className="absolute inset-0"
        width="28" height="28" viewBox="0 0 112 112" fill="none"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
      >
        <circle cx="56" cy="56" r="52" stroke="url(#navChipGold)" strokeWidth="3" fill="none" />
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
          <rect
            key={angle}
            x="54.5" y="1" width="3" height="7" rx="1.5"
            fill="url(#navChipGold)" opacity="0.5"
            transform={`rotate(${angle} 56 56)`}
          />
        ))}
      </motion.svg>

      {/* Inner ring — slow counter-clockwise */}
      <motion.svg
        className="absolute inset-0"
        width="28" height="28" viewBox="0 0 112 112" fill="none"
        animate={{ rotate: [0, -360] }}
        transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
      >
        <circle cx="56" cy="56" r="42" stroke="url(#navChipGold)" strokeWidth="2" fill="url(#navChipInner)" />
        <circle cx="56" cy="56" r="33" stroke="url(#navChipGold)" strokeWidth="0.5" fill="none" opacity="0.25" />
      </motion.svg>

      {/* "P" — static */}
      <svg className="absolute inset-0" width="28" height="28" viewBox="0 0 112 112" fill="none">
        <text
          x="56" y="56"
          textAnchor="middle" dominantBaseline="central"
          fill="url(#navTextGold)"
          fontFamily="'Playfair Display', 'Georgia', 'Times New Roman', serif"
          fontSize="46" fontWeight="600" letterSpacing="-1"
        >
          P
        </text>
      </svg>
    </div>
  )
}

export function GlobalTopBar() {
  const user = useAuthStore((s) => s.user)
  const balance = user?.balance ?? 0
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const navigate = useNavigate()
  const [notifOpen, setNotifOpen] = useState(false)

  return (
    <div className="bg-background/95 backdrop-blur-xl border-b border-foreground/[0.06] safe-area-top relative">
      {/* Gold accent line */}
      <div className="absolute bottom-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

      <div className="flex items-center justify-between h-12 px-4">
        {/* Left: Chip logo + brand text + online count */}
        <div className="flex items-center gap-2 min-w-0">
          <ChipLogo />
          <motion.span
            className="text-sm font-display font-semibold tracking-[0.06em] bg-clip-text text-transparent whitespace-nowrap"
            style={{
              backgroundImage: 'linear-gradient(90deg, var(--shimmer-base) 0%, var(--shimmer-base) 35%, var(--shimmer-highlight) 50%, var(--shimmer-base) 65%, var(--shimmer-base) 100%)',
              backgroundSize: '200% 100%',
            }}
            animate={{ backgroundPosition: ['100% 0%', '-100% 0%'] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          >
            PWG Lounge
          </motion.span>
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 flex-shrink-0">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
            </span>
            <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">127</span>
          </div>
        </div>

        {/* Right: Balance pill + theme toggle + notification bell */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => navigate('/wallet')}
            className="px-2.5 py-1.5 rounded-full bg-foreground/[0.06] border border-foreground/[0.12] flex items-center gap-1.5 hover:bg-foreground/[0.1] active:scale-[0.97] transition-all"
          >
            <span className="text-gold text-xs font-bold">₮</span>
            <span className="text-xs font-mono font-semibold text-foreground tracking-wide">
              ${(balance / 100).toFixed(2)}
            </span>
          </button>
          <button
            onClick={() => {
              const { resolvedTheme, setTheme } = useThemeStore.getState()
              setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
            }}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-foreground/10 transition-colors"
          >
            {useThemeStore((s) => s.resolvedTheme) === 'dark'
              ? <Sun className="w-4 h-4 text-foreground/50" />
              : <Moon className="w-4 h-4 text-foreground/50" />
            }
          </button>
          <button
            onClick={() => setNotifOpen(true)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-foreground/10 transition-colors relative"
          >
            <Bell className="w-4 h-4 text-foreground/50" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 border-2 border-background flex items-center justify-center">
                <span className="text-[9px] font-bold text-white leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              </span>
            )}
          </button>
        </div>
      </div>

      <NotificationPanel open={notifOpen} onOpenChange={setNotifOpen} />
    </div>
  )
}
